/**
 * The mountable template picker (V5-35 requirement 4).
 *
 * Drops into any record-entry surface as a single self-contained element. It
 * fetches its own templates, handles its own "mark used" call, and hands the
 * caller a ready-to-apply prefill — so wiring it into an existing entry form is
 * ONE callback, not a data-loading exercise.
 *
 *     <TemplatePicker type={type} onApply={applyPrefill} />
 *
 * SEE `index.ts` IN THIS FOLDER for the exact mount points in the entry forms,
 * with the handler bodies spelled out. Those files belong to another change
 * and are deliberately not edited here.
 *
 * PICKING NEVER SAVES — REQUIREMENT 2
 * ------------------------------------
 * `onApply` fires with the prefill and the flow stops there: the user sees a
 * filled-in form and confirms it themselves. `onApplyAndSave` is OPTIONAL and
 * renders a second, visually distinct one-tap button only when the host passes
 * it — that is the host's existing "fill and save" affordance (QuickAdd already
 * has one for history suggestions), still an explicit, deliberate tap on a
 * button labelled as such, never something this component does on its own.
 *
 * WHY THIS IS NOT THE SUGGESTIONS STRIP
 * --------------------------------------
 * The two are siblings and both belong in the entry flow. Suggestions are
 * DERIVED from what you have typed before; templates are what you SET UP. A
 * template exists for a bill you have never entered, carries a name of your
 * choosing, and can be retired. Mounting this alongside the existing "Frequent"
 * strip is the intended arrangement — it is not a replacement for it.
 */

import { useState } from "react";
import { Plus, Star, Zap } from "lucide-react";
import { toast } from "sonner";

import { cn, formatINR } from "@/lib/utils";
import { TemplateForm } from "./TemplateForm";
import { staleReason, templateAccent, type EntryTemplate, type TemplatePrefill, type TemplateType } from "./types";
import { useTemplateList, useUseTemplate } from "./useTemplates";

export interface TemplatePickerProps {
  /** Which entry kind the host form is currently on. */
  type: TemplateType;
  /**
   * Fill the form from this template. ALWAYS fires on a pick; the host applies
   * the values and lets the user confirm.
   */
  onApply: (prefill: TemplatePrefill, template: EntryTemplate) => void;
  /**
   * Optional one-tap "fill and save". When omitted, no such button renders and
   * the picker cannot trigger a save at all.
   */
  onApplyAndSave?: (prefill: TemplatePrefill, template: EntryTemplate) => void;
  /** Disables the one-tap button while the host's own save is in flight. */
  isSaving?: boolean;
  /** Hide the "New" chip on hosts that would rather link to the manage screen. */
  allowCreate?: boolean;
  className?: string;
}

export function TemplatePicker({
  type,
  onApply,
  onApplyAndSave,
  isSaving = false,
  allowCreate = true,
  className,
}: TemplatePickerProps) {
  const { data: templates = [], isLoading } = useTemplateList(type);
  const useTemplate = useUseTemplate();
  const [formOpen, setFormOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  function pick(template: EntryTemplate, alsoSave: boolean) {
    setPendingId(template.id);
    useTemplate.mutate(template.id, {
      onSuccess: (prefill) => {
        setPendingId(null);
        // The host receives the SERVER's prefill, not the list row: dead
        // category/account references are already stripped from it, so the
        // form it fills is always saveable.
        if (alsoSave && onApplyAndSave) onApplyAndSave(prefill, template);
        else onApply(prefill, template);
      },
      onError: (err: Error) => {
        setPendingId(null);
        toast.error(err.message);
      },
    });
  }

  const hasTemplates = templates.length > 0;
  if (isLoading && !hasTemplates) return null;
  if (!hasTemplates && !allowCreate) return null;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-1.5">
        <Star className="h-3 w-3 text-[var(--cp-ink-3)]" aria-hidden />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
          Templates
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {templates.map((template) => {
          const accent = templateAccent(template);
          const warning = staleReason(template);
          const busy = pendingId === template.id;
          return (
            <span
              key={template.id}
              className="inline-flex items-stretch overflow-hidden rounded-[7px] border border-[var(--cp-line)] bg-[var(--cp-surface)]"
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => pick(template, false)}
                title={
                  warning
                    ? `${template.name} — ${warning}`
                    : `Fill the form from "${template.name}"`
                }
                className="flex min-h-10 items-center gap-1.5 px-2.5 text-[12.5px] transition-colors hover:bg-[var(--cp-accent-wash)] disabled:opacity-50"
              >
                {accent && (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: accent }}
                    aria-hidden
                  />
                )}
                <span className="font-medium text-[var(--cp-ink)]">{template.name}</span>
                {template.amountPaise !== null && (
                  <span className="cp-num text-[var(--cp-ink-3)]">
                    {formatINR(template.amountPaise)}
                  </span>
                )}
                {warning && (
                  <span
                    className="text-[var(--cp-amber-ink)]"
                    aria-label={warning}
                    title={warning}
                  >
                    !
                  </span>
                )}
              </button>

              {onApplyAndSave && (
                <button
                  type="button"
                  disabled={busy || isSaving}
                  onClick={() => pick(template, true)}
                  title={`Fill and save "${template.name}" straight away`}
                  aria-label={`Fill and save ${template.name}`}
                  className="flex min-h-10 items-center border-l border-[var(--cp-line)] px-2 text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-accent-wash)] hover:text-[var(--cp-accent)] disabled:opacity-50"
                >
                  <Zap className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </span>
          );
        })}

        {allowCreate && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex min-h-10 items-center gap-1 rounded-[7px] border border-dashed border-[var(--cp-line)] px-2.5 text-[12.5px] text-[var(--cp-ink-3)] transition-colors hover:border-[var(--cp-accent)] hover:text-[var(--cp-accent)]"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {hasTemplates ? "New" : "Save a template"}
          </button>
        )}
      </div>

      {allowCreate && (
        <TemplateForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          defaultType={type}
        />
      )}
    </div>
  );
}
